import {
  byScheduledTime,
  formatDayLabel,
  isValidSchedule,
  toClockTime,
  toDayKey,
} from './scheduleTime'

type Schedulable = { id: string; scheduledFor: string }

export type ReschedulePlan<T extends Schedulable> = {
  /** The list with the new time applied, unsorted — the view sorts. */
  next: T[]
  id: string
  from: string
  to: string
  /**
   * Left its day section. Only "Just arrived" on an overdue row can do this
   * today, and it is the case the eye cannot follow — never animated, always
   * toasted.
   */
  crossedDay: boolean
  /** Visibly moved: changed board order, or left its day section (which is
      a visual move even when board order is preserved). */
  moved: boolean
  /** 0-based position within its (new) day section, for the announcement. */
  dayIndex: number
  /** Row count of that day section after the move. */
  dayCount: number
}

/**
 * Works out what a reschedule does before committing it, so the caller can
 * decide how loudly to react: an unmoved tile needs nothing, a visible slide
 * is its own confirmation, and only an unfollowable move needs a toast.
 *
 * Returns null when there is nothing to do (unknown id, invalid time,
 * same time).
 */
export function planReschedule<T extends Schedulable>(
  deliveries: T[],
  id: string,
  to: string,
): ReschedulePlan<T> | null {
  if (!isValidSchedule(to)) return null

  const target = deliveries.find((delivery) => delivery.id === id)
  if (!target || target.scheduledFor === to) return null

  const next = deliveries.map((delivery) =>
    delivery.id === id ? { ...delivery, scheduledFor: to } : delivery,
  )

  const boardIndex = (list: T[]) =>
    [...list].sort(byScheduledTime).findIndex((delivery) => delivery.id === id)

  const crossedDay = toDayKey(target.scheduledFor) !== toDayKey(to)
  const toDay = toDayKey(to)
  const daySorted = [...next]
    .filter((delivery) => toDayKey(delivery.scheduledFor) === toDay)
    .sort(byScheduledTime)

  return {
    next,
    id,
    from: target.scheduledFor,
    to,
    crossedDay,
    moved: crossedDay || boardIndex(deliveries) !== boardIndex(next),
    dayIndex: daySorted.findIndex((delivery) => delivery.id === id),
    dayCount: daySorted.length,
  }
}

function ordinal(oneBased: number): string {
  const suffix =
    oneBased % 100 >= 11 && oneBased % 100 <= 13
      ? 'th'
      : (['th', 'st', 'nd', 'rd'][oneBased % 10] ?? 'th')
  return `${oneBased}${suffix}`
}

/**
 * What a screen reader hears. Day named only when it changed, position only
 * when it changed — otherwise both are noise on every small delay.
 */
export function announceReschedule<T extends Schedulable>(
  plan: ReschedulePlan<T>,
  now: Date = new Date(),
): string {
  const base = `${plan.id} rescheduled to ${toClockTime(plan.to)}`
  if (plan.crossedDay) {
    return `${base} on ${formatDayLabel(toDayKey(plan.to), now)}`
  }
  if (!plan.moved) return base
  return `${base}, now ${ordinal(plan.dayIndex + 1)} of ${plan.dayCount}`
}

/** Short form for the toast, where the delivery is already the subject. */
export function summariseReschedule<T extends Schedulable>(
  plan: ReschedulePlan<T>,
  now: Date = new Date(),
): string {
  const clock = toClockTime(plan.to)
  if (!plan.crossedDay) return `${plan.id} → ${clock}`
  // The day label leads with Today / Tomorrow, which is exactly the part
  // the toast needs; drop the date tail to keep it glanceable.
  const day = formatDayLabel(toDayKey(plan.to), now).split(' · ')[0]
  return `${plan.id} → ${day} ${clock}`
}
